import { useState } from 'react'
import { supabase } from './supabase'

export default function CreateTable({ session, onBack, onCreated }) {
  const [name, setName] = useState('')
  const [wager, setWager] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [payoutFormat, setPayoutFormat] = useState('winner_takes_all')
  const [livesEnabled, setLivesEnabled] = useState(false)
  const [livesCount, setLivesCount] = useState(3)
  const [rebuyAllowed, setRebuyAllowed] = useState(false)
  const [rebuyCost, setRebuyCost] = useState('')
  const [rebuyLives, setRebuyLives] = useState(1)
  const [splitConfig, setSplitConfig] = useState([
    { place: 1, pct: 60 }, { place: 2, pct: 30 }, { place: 3, pct: 10 }
  ])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = async () => {
    setError(null)
    if (!name.trim()) return setError('Give your table a name')
    if (!wager || parseFloat(wager) <= 0) return setError('Set a wager amount')
    if (payoutFormat === 'custom_split') {
      const total = splitConfig.reduce((a, s) => a + s.pct, 0)
      if (total !== 100) return setError(`Split must total 100% (currently ${total}%)`)
    }
    setSaving(true)
    const { data, error: e } = await supabase.from('crossbet_tables').insert({
      created_by: session.user.id,
      name: name.trim(),
      wager_amount: parseFloat(wager),
      scheduled_at: scheduledAt || null,
      payout_format: payoutFormat,
      lives_enabled: livesEnabled,
      lives_per_player: livesEnabled ? livesCount : 1,
      rebuy_allowed: livesEnabled ? rebuyAllowed : false,
      rebuy_cost: rebuyAllowed ? parseFloat(rebuyCost || wager) : null,
      rebuy_lives: rebuyAllowed ? rebuyLives : null,
      scorekeeper_id: session.user.id,
      split_config: payoutFormat === 'custom_split' ? splitConfig : null,
      status: 'pending',
    }).select().single()
    setSaving(false)
    if (e) return setError(e.message)

    // Add creator as accepted player
    await supabase.from('crossbet_table_players').insert({
      table_id: data.id,
      player_id: session.user.id,
      status: 'accepted',
      lives: livesEnabled ? livesCount : 1,
      total_paid: parseFloat(wager),
      joined_at: new Date().toISOString(),
    })

    onCreated(data.id)
  }

  return (
    <div style={s.screen}>
      <div style={s.body}>

        {/* Name */}
        <div style={s.field}>
          <div style={s.label}>TABLE NAME</div>
          <input style={s.input} placeholder="e.g. Poker Night · Friday Cubilete"
            value={name} onChange={e => setName(e.target.value)} />
        </div>

        {/* Date */}
        <div style={s.field}>
          <div style={s.label}>STARTS AT <span style={s.optional}>(optional)</span></div>
          <input style={s.input} type="datetime-local" value={scheduledAt} min={new Date().toISOString().slice(0,16)} onChange={e => setScheduledAt(e.target.value)} />
        </div>

        {/* Wager */}
        <div style={s.field}>
          <div style={s.label}>BUY-IN / WAGER</div>
          <div style={s.wagerWrap}>
            <span style={s.wagerSign}>$</span>
            <input style={s.wagerInput} type="number" min="1" placeholder="0"
              value={wager} onChange={e => setWager(e.target.value)} />
          </div>
        </div>

        {/* Payout Format */}
        <div style={s.field}>
          <div style={s.label}>PAYOUT FORMAT</div>
          <div style={s.optionRow}>
            {[
              { key: 'winner_takes_all', label: '🏆 Winner Takes All' },
              { key: 'custom_split', label: '✂️ Custom Split' },
            ].map(o => (
              <button key={o.key} onClick={() => setPayoutFormat(o.key)}
                style={{ ...s.optionBtn, ...(payoutFormat === o.key ? s.optionBtnActive : {}) }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Split Config */}
        {payoutFormat === 'custom_split' && (
          <div style={s.splitCard}>
            <div style={s.label}>SPLIT PERCENTAGES</div>
            {splitConfig.map((sp, i) => (
              <div key={i} style={s.splitRow}>
                <div style={s.splitPlace}>#{sp.place}</div>
                <input style={s.splitInput} type="number" min="0" max="100"
                  value={sp.pct} onChange={e => {
                    const newSplit = [...splitConfig]
                    newSplit[i].pct = parseInt(e.target.value) || 0
                    setSplitConfig(newSplit)
                  }} />
                <span style={s.splitPct}>%</span>
                {splitConfig.length > 2 && (
                  <button style={s.splitRemove} onClick={() => setSplitConfig(splitConfig.filter((_, j) => j !== i))}>✕</button>
                )}
              </div>
            ))}
            <button style={s.splitAdd} onClick={() => setSplitConfig([...splitConfig, { place: splitConfig.length + 1, pct: 0 }])}>
              + Add Place
            </button>
          </div>
        )}

        {/* Lives Option */}
        <div style={s.toggleRow}>
          <div>
            <div style={s.toggleLabel}>LIVES</div>
            <div style={s.toggleSub}>Players get multiple chances before elimination</div>
          </div>
          <button style={{ ...s.toggleBtn, ...(livesEnabled ? s.toggleBtnOn : {}) }}
            onClick={() => setLivesEnabled(v => !v)}>
            {livesEnabled ? 'ON' : 'OFF'}
          </button>
        </div>

        {livesEnabled && (
          <div style={s.livesCard}>
            <div style={s.field}>
              <div style={s.label}>LIVES PER PLAYER</div>
              <div style={s.stepper}>
                <button style={s.stepBtn} onClick={() => setLivesCount(v => Math.max(1, v - 1))}>−</button>
                <div style={s.stepVal}>{livesCount}</div>
                <button style={s.stepBtn} onClick={() => setLivesCount(v => v + 1)}>+</button>
              </div>
            </div>

            <div style={s.toggleRow}>
              <div>
                <div style={s.toggleLabel}>REBUY</div>
                <div style={s.toggleSub}>Eliminated players can buy back in</div>
              </div>
              <button style={{ ...s.toggleBtn, ...(rebuyAllowed ? s.toggleBtnOn : {}) }}
                onClick={() => setRebuyAllowed(v => !v)}>
                {rebuyAllowed ? 'ON' : 'OFF'}
              </button>
            </div>

            {rebuyAllowed && (
              <>
                <div style={s.field}>
                  <div style={s.label}>REBUY COST <span style={s.optional}>(leave blank = same as buy-in)</span></div>
                  <div style={s.wagerWrap}>
                    <span style={s.wagerSign}>$</span>
                    <input style={s.wagerInput} type="number" min="0"
                      placeholder={wager || '0'} value={rebuyCost}
                      onChange={e => setRebuyCost(e.target.value)} />
                  </div>
                </div>
                <div style={s.field}>
                  <div style={s.label}>LIVES ON REBUY</div>
                  <div style={s.optionRow}>
                    <button style={{ ...s.optionBtn, ...(rebuyLives === 1 ? s.optionBtnActive : {}) }}
                      onClick={() => setRebuyLives(1)}>1 Life</button>
                    <button style={{ ...s.optionBtn, ...(rebuyLives === livesCount ? s.optionBtnActive : {}) }}
                      onClick={() => setRebuyLives(livesCount)}>Full Reset ({livesCount})</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Scorekeeper note */}
        <div style={s.scorekeeperNote}>
          🎯 You are the Scorekeeper — you and players can mark eliminations. All players are informed.
        </div>

        {error && <div style={s.error}>{error}</div>}

        <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Creating...' : 'Create Table & Invite Players →'}
        </button>

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase' },
  optional: { fontSize: 10, fontWeight: 400, color: '#B0ACA4', letterSpacing: 0, textTransform: 'none' },
  input: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  wagerWrap: { position: 'relative' },
  wagerSign: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#C19A6B' },
  wagerInput: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px 13px 36px', fontSize: 20, fontFamily: "'Bebas Neue', sans-serif", color: '#9B7D52', outline: 'none', width: '100%', boxSizing: 'border-box' },
  optionRow: { display: 'flex', gap: 8 },
  optionBtn: { flex: 1, padding: '11px 8px', background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, fontSize: 13, fontWeight: 600, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  optionBtnActive: { background: 'rgba(0,96,57,0.06)', border: '1.5px solid #00563F', color: '#00563F' },
  splitCard: { background: '#FFFFFF', borderRadius: 16, padding: 16, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 10 },
  splitRow: { display: 'flex', alignItems: 'center', gap: 8 },
  splitPlace: { fontSize: 13, fontWeight: 700, color: '#1A1814', width: 24 },
  splitInput: { background: '#FDF9E7', border: '1.5px solid #D0CCC0', borderRadius: 8, padding: '8px 10px', fontSize: 15, fontFamily: 'inherit', outline: 'none', width: 70, textAlign: 'center' },
  splitPct: { fontSize: 13, color: '#7A7570' },
  splitRemove: { background: 'none', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' },
  splitAdd: { background: 'transparent', border: '1px dashed #D0CCC0', borderRadius: 8, padding: '8px', fontSize: 12, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', borderRadius: 12, padding: '12px 16px', border: '1px solid #E0DDD4' },
  toggleLabel: { fontSize: 13, fontWeight: 700, color: '#1A1814' },
  toggleSub: { fontSize: 11, color: '#7A7570', marginTop: 2 },
  toggleBtn: { background: '#EDE8D8', border: 'none', borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 700, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  toggleBtnOn: { background: '#00563F', color: 'white' },
  livesCard: { background: '#FFFFFF', borderRadius: 16, padding: 16, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 12 },
  stepper: { display: 'flex', alignItems: 'center', gap: 16 },
  stepBtn: { background: '#EDE8D8', border: 'none', borderRadius: 8, width: 36, height: 36, fontSize: 20, fontWeight: 700, color: '#1A1814', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stepVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#1A1814', minWidth: 40, textAlign: 'center' },
  scorekeeperNote: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 12, padding: '12px 14px', fontSize: 12, color: '#9B7D52', lineHeight: 1.5 },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#C0392B' },
  saveBtn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
}
